import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type AccountPurpose, type BankAccount, type Company } from '../api';
import type { Dictionary } from '../i18n';
import * as Icon from '../components/Icons';

const PURPOSES: AccountPurpose[] = ['MAIN', 'VAT', 'TAX_REFUND'];

export function BankAccountsPanel({ company, t }: { company: Company; t: Dictionary }) {
  const [accounts, setAccounts] = useState<BankAccount[] | null>(null);
  const [form, setForm] = useState<{
    iban: string;
    currency: string;
    purpose: AccountPurpose;
    bankName: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setAccounts(await api.bankAccounts(company.id));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const editable = company.roles.some((role) => role === 'OWNER' || role === 'ADMIN' || role === 'ACCOUNTANT');

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.bank.title}</h3>
          <p>{t.bank.hint}</p>
        </div>
        {editable && !form && (
          <div className="sp">
            <button
              type="button"
              className="btn"
              onClick={() => setForm({ iban: '', currency: 'PLN', purpose: 'MAIN', bankName: '' })}
            >
              <Icon.Plus />
              {t.bank.add}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      <div className="panel">
        {accounts?.length === 0 && !form ? (
          <div className="empty">
            <Icon.Fee />
            <b>{t.bank.none}</b>
            <p>{t.bank.hint}</p>
          </div>
        ) : (
          accounts &&
          accounts.length > 0 && (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.bank.iban}</th>
                    <th>{t.bank.bankName}</th>
                    <th>{t.bank.currency}</th>
                    <th>{t.bank.purpose}</th>
                    <th>{t.costs.status}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr key={account.id}>
                      {/* Zapis po cztery znaki czyta się tak samo jak na przelewie */}
                      <td className="mono">{account.ibanFormatted}</td>
                      <td>{account.bankName ?? '—'}</td>
                      <td>{account.currency}</td>
                      <td>
                        <span className="chip st-info">
                          <i />
                          {t.bank.purposes[account.purpose]}
                        </span>
                      </td>
                      <td>
                        {/* Numer przechodzi kontrolę sumy już przy zapisie — tu tylko o tym mówimy */}
                        <span className="chip st-ok">
                          <i />
                          {t.bank.checksumOk}
                        </span>
                      </td>
                      <td className="act">
                        <div className="row">
                          {editable && (
                            <button
                              type="button"
                              className="btn danger sm"
                              disabled={busy}
                              onClick={() => void run(() => api.removeBankAccount(company.id, account.id))}
                            >
                              {t.bank.remove}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {form && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void run(async () => {
                await api.addBankAccount(company.id, {
                  iban: form.iban,
                  currency: form.currency,
                  purpose: form.purpose,
                  bankName: form.bankName.trim() || undefined,
                });
                setForm(null);
              });
            }}
          >
            <div className="fields">
              <div className="field">
                <label htmlFor="iban">{t.bank.iban}</label>
                <input
                  id="iban"
                  className="inp mono"
                  value={form.iban}
                  onChange={(event) => setForm({ ...form, iban: event.target.value })}
                  placeholder="PL00 0000 0000 0000 0000 0000 0000"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="purpose">{t.bank.purpose}</label>
                <select
                  id="purpose"
                  className="inp"
                  value={form.purpose}
                  onChange={(event) => setForm({ ...form, purpose: event.target.value as AccountPurpose })}
                >
                  {PURPOSES.map((purpose) => (
                    <option key={purpose} value={purpose}>
                      {t.bank.purposes[purpose]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="currency">{t.bank.currency}</label>
                <input
                  id="currency"
                  className="inp mono"
                  value={form.currency}
                  onChange={(event) => setForm({ ...form, currency: event.target.value.toUpperCase() })}
                  maxLength={3}
                />
              </div>
              <div className="field">
                <label htmlFor="bankName">{t.bank.bankName}</label>
                <input
                  id="bankName"
                  className="inp"
                  value={form.bankName}
                  onChange={(event) => setForm({ ...form, bankName: event.target.value })}
                />
              </div>
            </div>
            <div className="bar2">
              <button type="submit" className="btn" disabled={busy}>
                {busy ? t.companies.saving : t.bank.save}
              </button>
              <div className="sp">
                <button type="button" className="btn ghost" onClick={() => setForm(null)} disabled={busy}>
                  {t.companies.cancel}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>

      <div className="panel future">
        <div className="hd2">
          <b>{t.bank.whiteListTitle}</b>
          <span className="soon">{t.invoices.soon}</span>
        </div>
        <p className="sm muted">{t.bank.whiteListText}</p>
      </div>
    </>
  );
}
